// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, repeat, Subject, timer } from 'rxjs';
import { filter, map, pairwise, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {HeaderService, IContextHelpMenuItem} from 'src/app/header.service';
import {UserService} from 'src/services/user-service';
import {IManageUser, UserRole, UserSource} from 'src/types/user';
import {AuthService} from 'src/services/auth-service';
import {TranslateService} from '@ngx-translate/core';
import {NavigationEnd, NavigationStart, Router, RoutesRecognized} from '@angular/router';
import {environment} from 'src/environments/environment';
import {SupportInfo} from 'src/types/contact';
import {D3eService} from 'src/services/d3e-service';
import {IActivelockAlert, IState} from './notifications-models';
import { ISoftwareVersion, ISubscription, LicenseType, StripeProductTier } from 'src/types/subscription';
import {FocusTrap, FocusTrapFactory} from '@angular/cdk/a11y';
import {Title} from '@angular/platform-browser';
import { IDownloadVersion, NewsComponent } from './dialogs/news/news.component';
import {MatDialog} from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import {AppUpdateService} from 'src/services/app-update.service';
import {
    isCustomer,
    isD3EFreeUser,
    isMSP,
    isReseller,
    isResellerOrMSP,
    userExists
} from 'src/shared/permissions-helper';
import authHelper from './auth/authHelper';
import {ConfigurationService} from '../services/configuration-service';
import {get_uuid_filename, getFirstValidSubscription, isSubscriptionExpired} from '../shared/subscription.methods';
import multiDownload from 'multi-download';
import {ActionService} from '../services/action-service';
import {Theme, ThemeService} from '../services/theme.service';
import {ALL_ORGS_ID, IGroup, OrgType} from '../types/group';
import {Partners} from '../types/partners';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {loadUser} from './store/user/user.actions';
import {loadOrganizations} from './store/organizations/organizations.actions';
import {AppState} from './store/app.state';
import {selectUser} from './store/user/user.selectors';
import {selectOrganizations} from './store/organizations/organizations.selectors';
import {memo} from './shared/memoize/memo';
import {FeatureFlags} from '../types/auth';
import {IRestConfig} from '../types/config';
import {RestConfigService} from '../services/config-service';
import { CspReportingService } from '../services/csp-reporting-service';
import { UserTourComponent } from './shared/components/user-tour/user-tour.component';
import {
  SecurityRequestsStatusListDialogComponent
} from './shared/components/security-requests-status-list-dialog/security-requests-status-list-dialog.component';
import {SecurityApproverState} from '../types/security-requests';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  showNav: BehaviorSubject<boolean>;
  showToolbar: BehaviorSubject<boolean>;
  showBanners: BehaviorSubject<boolean>;
  showRefreshButton$: BehaviorSubject<boolean>;
  visibilityChanged$: BehaviorSubject<void> = new BehaviorSubject<void>(null);
  maintenanceMode$: BehaviorSubject<boolean> = this.authService.maintenanceMode$;
  readonly alertsObservable$: Observable<void>;
  private readonly _stop = new Subject<void>();
  private readonly _start = new Subject<void>();
  private running = false;
  supportInfo = SupportInfo;
  consoleReleaseNotesURL = 'https://support.cigent.com/support/solutions/articles/73000591609-console-release-notes';
  consoleDocsURL = 'https://support.cigent.com/support/solutions/73000318756';
  cigentPBASoftwareURL = 'https://support.cigent.com/support/solutions/73000347073';
  title = '';
  subtitle = '';
  navMenuText = '';
  showUserFilter = false;
  userSelectorDefaultOption = 'All Organizations';
  icon = 'developer_board';
  user$: Observable<IManageUser> = this.store.select(selectUser()).pipe(
    switchMap(user => {
      // In the force two-factor flow, if the user refreshes after setting their 2FA, but before hitting the Done
      // button then they would stay logged in, but in a strange state of not being able to navigate around.
      // The auth token would still have the ftf claim set, but the user from the api would not have it set.
      // They must log back in.
      if (authHelper.getFeatureFlagFromToken('ftf') && !user?.force_two_factor) {
        this.authService.logout();
        return EMPTY;
      } else if (!!user) {
        return of(user);
      } else {
        return of(null);
      }
    })
  );
  organizations$: Observable<IGroup[]> = this.store.select(selectOrganizations()).pipe(
    withLatestFrom(this.user$),
    map(([orgs, user]: [IGroup[], IManageUser]) => orgs.filter(o => o?.id != user?.group_id)),
  );
  selectedOrgId = ALL_ORGS_ID;
  currentAllUsersId = 'All';
  lastAllUsersId = 'All';
  isAuthenticated = false;
  isLoggedIn$: Observable<boolean>;
  isExpanded = true;
  url = '/';
  alerts: IActivelockAlert[] = [];
  alertsMaxDisplay = 8;
  alertsCount = 0;
  alertPanelShown = false;
  detailsShown = false;
  dismissAllPanel = false;
  details: IActivelockAlert;
  state: IState;
  sensorTypes = {};
  showExpirationBanner = false;
  showUpdateBanner = false;
  notificationsFocusTrap: FocusTrap = null;
  notificationDetailsFocusTrap: FocusTrap = null;
  @ViewChild('alertPanel') alertPanel: ElementRef;
  @ViewChild('dismissAllMenu') dismissAllMenu: ElementRef;
  @ViewChild('closeDetailsLink') closeDetailsLink: ElementRef;
  @ViewChild('detailsDiv') detailsDiv: ElementRef;
  @ViewChild('userTour') userTour: UserTourComponent;
  language: string;
  isCigentUser: boolean;
  theme: Observable<Theme>;
  showThemeSelector: boolean;
  cigentPartner = Partners.Cigent;
  doqubizPartner = Partners.Doqubiz;
  digistorPartner = Partners.Digistor;
  kanguruPartner = Partners.Kanguru;
  lenovoPartner = Partners.Lenovo;
  vrsPartner = Partners.VRS;
  partnerId: Partners = Partners.Cigent;
  consoleVersion = JSON.parse(environment.__VERSION__); // JSON.parse is used to remove the double quotes
  latestCigentVersion: string;
  restApiVersion: string;
  msiVersion: string;
  pbaVersions: IDownloadVersion[];
  winCliVersions: IDownloadVersion[];
  ubuntuCliVersions: IDownloadVersion[];
  rhelCliVersions: IDownloadVersion[];
  winFdeVersions: IDownloadVersion[];
  ubuntuFdeVersions: IDownloadVersion[];
  rhelFdeVersions: IDownloadVersion[];
  winAgentVersions: IDownloadVersion[];
  ubuntuAgentVersions: IDownloadVersion[];
  rhelAgentVersions: IDownloadVersion[];
  showPaymentMethods: boolean;
  public contextSensitiveHelp$: BehaviorSubject<IContextHelpMenuItem[]>;
  orgSelectorDisabled$ = this.headerService.userFilterDisabled;
  onlyFdeSubscriptions$: Observable<boolean>;

  fResellers: string;
  fMSPs: string;
  fMyAccount: string;
  fDashboard: string;
  fOrganizations: string;
  fDevices: string;
  fEndpoints: string;
  fSSSD: string;
  fHistory: string;
  fEventViewer: string;
  fLicenses: string;
  fPolicies: string;
  fGroups: string;
  fDeviceSets: string;
  fManageGroups: string;
  f3RDIntegrations: string;
  fIntegrations: string;
  fUsers: string;
  fAdminPage: string;
  fAdmin: string;
  fSettings: string;
  fInvoices: string;
  fViewInvoices: string;
  fEncryption: string;
  fDecryption: string;
  fHelp: string;
  fSupport: string;
  fNewVersion: string;
  fUpdate: string;
  fExpiredA: string;
  fExpiredB: string;
  fExpiredC: string;
  fSuperUser: string;
  fManageProfile: string;
  fChangePassword: string;
  fManage2FA: string;
  fNotifications: string;
  fPayment: string;
  fSignOut: string;
  fClearNotifications: string;
  fBack: string;
  fReasons: string;
  fHost: string;
  fDate: string;
  fTime: string;
  fNetworks: string;
  fControl: string;
  fUserInfo: string;
  fActiveLockEngage: string;
  fOn: string;
  fShowMore: string;

  restConfig: IRestConfig;

  constructor(
    private authService: AuthService,
    private d3eService: D3eService,
    private userService: UserService,
    private headerService: HeaderService,
    private focusTrapFactory: FocusTrapFactory,
    private translate: TranslateService,
    private configuration: ConfigurationService,
    private readonly restConfigService: RestConfigService,
    public router: Router,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
    public dialog: MatDialog,
    private http: HttpClient,
    private appUpdateService: AppUpdateService,
    private actionService: ActionService,
    private themeService: ThemeService,
    private readonly store: Store<AppState>,
    private readonly reportingService: CspReportingService
    ) {
      this.contextSensitiveHelp$ = headerService.contextSensitiveHelp$;
      this.showRefreshButton$ = headerService.showRefreshButton$;
      this.translate.get(['APP_RESELLERS', 'APP_MSPS', 'APP_MYACCOUNT', 'APP_DASHBOARD', 'APP_ORGANIZATIONS',
        'APP_DEVICES', 'APP_ENDPOINTS', 'APP_SSSD', 'APP_HISTORY', 'APP_EVENT_VIEWER', 'APP_LICENSES', 'APP_POLICIES', 'APP_GROUPS',
        'APP_DEVICE', 'APP_MANAGE_GROUPS', 'APP_3RD_INT', 'APP_INT', 'APP_USERS', 'APP_ADMIN_PAGE', 'APP_ADMIN', 'APP_SETTINGS',
        'APP_INVOICES', 'APP_VIEW_INVOICES', 'APP_FILE_ENCRYPTION', 'APP_FILE_DECRYPTION', 'APP_HELP', 'APP_HELP', 'APP_CONTACT',
        'APP_NEW', 'APP_UPDATE', 'APP_EXPIRED_A', 'APP_EXPIRED_B', 'APP_EXPIRED_C', 'APP_SUPER', 'APP_MANAGE_PF', 'APP_CHANGE_PW',
        'APP_TFA', 'APP_NOTIFICATIONS', 'APP_PAYMENT', 'APP_SIGNOUT', 'APP_CLEAR_NOTIF', 'APP_BACK', 'APP_REASONS', 'APP_HOST',
        'APP_DATE', 'APP_TIME', 'APP_NETWORKS', 'APP_CONTROL', 'APP_USER_INFO', 'APP_ACTIVELOCK_ENGAGE', 'APP_ON',
        'APP_SHOWMORE']).subscribe(vals => {
        this.fResellers = vals['APP_RESELLERS'];
        this.fMSPs = vals['APP_MSPS'];
        this.fMyAccount = vals['APP_MYACCOUNT'];
        this.fDashboard = vals['APP_DASHBOARD'];
        this.fOrganizations = vals['APP_ORGANIZATIONS'];
        this.fDevices = vals['APP_DEVICES'];
        this.fEndpoints = vals['APP_ENDPOINTS'];
        this.fSSSD = vals['APP_SSSD'];
        this.fHistory = vals['APP_HISTORY'];
        this.fEventViewer = vals['APP_EVENT_VIEWER'];
        this.fLicenses = vals['APP_LICENSES'];
        this.fPolicies = vals['APP_POLICIES'];
        this.fGroups = vals['APP_GROUPS'];
        this.fDeviceSets = vals['APP_DEVICE'];
        this.fManageGroups = vals['APP_MANAGE_GROUPS'];
        this.f3RDIntegrations = vals['APP_3RD_INT'];
        this.fIntegrations = vals['APP_INT'];
        this.fUsers = vals['APP_USERS'];
        this.fAdminPage = vals['APP_ADMIN_PAGE'];
        this.fAdmin = vals['APP_ADMIN'];
        this.fSettings = vals['APP_SETTINGS'];
        this.fInvoices = vals['APP_INVOICES'];
        this.fViewInvoices = vals['APP_VIEW_INVOICES'];
        this.fEncryption = vals['APP_FILE_ENCRYPTION'];
        this.fDecryption = vals['APP_FILE_DECRYPTION'];
        this.fHelp = vals['APP_HELP'];
        this.fSupport = vals['APP_CONTACT'];
        this.fNewVersion = vals['APP_NEW'];
        this.fUpdate = vals['APP_UPDATE'];
        this.fExpiredA = vals['APP_EXPIRED_A'];
        this.fExpiredB = vals['APP_EXPIRED_B'];
        this.fExpiredC = vals['APP_EXPIRED_C'];
        this.fSuperUser = vals['APP_SUPER'];
        this.fManageProfile = vals['APP_MANAGE_PF'];
        this.fChangePassword = vals['APP_CHANGE_PW'];
        this.fManage2FA = vals['APP_TFA'];
        this.fNotifications = vals['APP_NOTIFICATIONS'];
        this.fPayment = vals['APP_PAYMENT'];
        this.fSignOut = vals['APP_SIGNOUT'];
        this.fClearNotifications = vals['APP_CLEAR_NOTIF'];
        this.fBack = vals['APP_BACK'];
        this.fReasons = vals['APP_REASONS'];
        this.fHost = vals['APP_HOST'];
        this.fDate = vals['APP_DATE'];
        this.fTime = vals['APP_TIME'];
        this.fNetworks = vals['APP_NETWORKS'];
        this.fControl = vals['APP_CONTROL'];
        this.fUserInfo = vals['APP_USER_INFO'];
        this.fActiveLockEngage = vals['APP_ACTIVELOCK_ENGAGE'];
        this.fOn = vals['APP_ON'];
        this.fShowMore = vals['APP_SHOWMORE'];
        });
    this.showNav = this.authService.showNav;
    this.showToolbar = this.authService.showToolbar;
    this.showBanners = this.authService.showBanners;
    this.appUpdateService.watchForUpdates();
    this.showUpdateBanner = this.appUpdateService.showUpdateBanner();
    this.theme = themeService.getTheme();
    this.showPaymentMethods = this.configuration.showPaymentMethods;
    this.authService.setSentryTagsFromToken();
    // Detect an infinite loop and set localStorage with
    // the value to be checked and used in the role-guard
    this.router.events
      .pipe(
        tap(event => {
          if (event instanceof NavigationEnd) {
            this.url = this.router.url;
            this.setTabTitle();
            this.showUpdateBanner = this.appUpdateService.showUpdateBanner();
            if((this.isNewsPage() && this.authService.isAuthenticated())) {
              this.showNav.next(true);
            } else if (this.isFullWidthPage()) {
              this.showNav.next(false);
            } else {
              this.showNav.next(true);
            }
          }
        }),
        filter((evt: any) => evt instanceof RoutesRecognized),
        pairwise()
      )
      .subscribe((events: RoutesRecognized[]) => {
        const previousUrl = events[0].urlAfterRedirects;
        const nextUrl = events[1].urlAfterRedirects;

        if (previousUrl === nextUrl) {
          const loopDetectedItem = localStorage.getItem('loopDetected');
          const loopDetected = loopDetectedItem ? JSON.parse(loopDetectedItem) : {url: nextUrl, count: 0};
          if (loopDetected.url === nextUrl) {
            loopDetected.count++;
          } else {
            localStorage.removeItem('loopDetected');
          }
          localStorage.setItem('loopDetected', JSON.stringify(loopDetected));
        } else {
          localStorage.removeItem('loopDetected');
        }
    });

    // As an MSP on the group-details page, a selectedOrg must be selected.
    // On a page refresh the selectedOrg is lost but the user is still on the page and the selectedOrg and group are lost.
    // So, instead on a page refresh, just navigate to the parent page, groups, and make them select another organization and group.
    router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          if (event.url.startsWith('/group-details/device/')) {
            if (!router.navigated) {
              router.navigate(['groups']);
            }
          }
        }
    });

    this.initTranslate();

    document.addEventListener('visibilitychange', this.triggerVisibility.bind(this));

    if (this.authService.isAuthenticated()) {
      this.store.dispatch(loadUser());
    }

    this.alertsObservable$ = timer(0, 30000)
        .pipe(
            map(() => this.getAlerts()),
            takeUntil(this._stop),
            repeat({ delay: () => this._start })
        );

    this.onlyFdeSubscriptions$ = this.d3eService.get_subscriptions()
      .pipe(
          map(subscriptions => {
            return subscriptions.every(s => s.stripe_product.tier === StripeProductTier.FDE);
        }),
        untilDestroyed(this)
      );
  }

  triggerVisibility(): void {
    this.visibilityChanged$.next(null);
  }

  ngOnInit() {
    this.isLoggedIn$ = this.authService.isLoggedIn$;
    this.restConfigService.getRestConfig().subscribe(c => this.restConfig = c);

    const versions$ = this.d3eService.get_d3e_software_version();
    this.authService.isLoggedIn$
        .pipe(
            tap(isLoggedIn => {
              if (!isLoggedIn) {
                this.showExpirationBanner = false;
                this.orgSelectChanged(ALL_ORGS_ID);
                this.alerts = [];
                this.alertsCount = 0;
              } else if (this.authService.isAuthenticated()) {
                this.store.dispatch(loadUser());
              }
            }),
            switchMap(_ => versions$),
            untilDestroyed(this)
        ).subscribe((v: ISoftwareVersion) => {
          this.latestCigentVersion = `${v.major}.${v.minor}.${v.revision}`;
          this.restApiVersion = `${v.rest_api_version.major}.${v.rest_api_version.minor}.${v.rest_api_version.revision}` + (v.rest_api_version.date ? ` (${v.rest_api_version.date})` : '');
          this.msiVersion = v.msi_version;
          this.pbaVersions = v.pba_versions;
          this.winCliVersions = v.win_cli_versions;
          this.ubuntuCliVersions = v.ubuntu_cli_versions;
          this.rhelCliVersions = v.rhel_cli_versions;
          this.winFdeVersions = v.win_fde_versions;
          this.ubuntuFdeVersions = v.ubuntu_fde_versions;
          this.rhelFdeVersions = v.rhel_fde_versions;
          this.winAgentVersions = v.win_agent_versions;
          this.ubuntuAgentVersions = v.ubuntu_agent_versions;
          this.rhelAgentVersions = v.rhel_agent_versions;
          if (environment.__DEV__) {
            console.log(`Console v${this.consoleVersion}, API v${this.restApiVersion}, Data Defense v${this.latestCigentVersion}`);
          }
        });

    this.user$.subscribe(user => !!user ? this.setUserAttributes(user) : null);

    this.setTabTitle();

    const navOpen = localStorage.getItem('nav-open');

    if (navOpen !== undefined && navOpen != null) {
      this.isExpanded = JSON.parse(navOpen);
    }

    this.alertsObservable$.subscribe();

    this.visibilityChanged$.pipe(
      withLatestFrom(this.user$)
    ).subscribe(([_, user]: [void, IManageUser]) => this.handleVisibilityChange(user));

    this.reportingService.startObserving();
  }

  ngOnDestroy(): void {
    this.stopAlerts();
  }

  onUpdateApp() {
    this.appUpdateService.updateApp();
    this.showUpdateBanner = this.appUpdateService.showUpdateBanner();
  }

  setTabTitle() {
    this.isLoggedIn$
        .pipe(untilDestroyed(this))
        .subscribe( isLoggedIn => {
      // Hide certain tabs in the menu from non-Cigent users
      const source = authHelper.getSourceFromToken();
      this.isCigentUser = source === UserSource.Cigent;

      const switchVar = this.configuration.configuration;
      this.partnerId = this.configuration.partnerId ?? Partners.Cigent;
      const interpolateParams = {titlePrefix: this.configuration.titlePrefix};
      switch (switchVar) {
        case 'dev':
          if (!isLoggedIn) {
            if (this.url === '/subscriptions/free') {
              this.setTitle(this.translate.instant('TITLE_APP_FREE', interpolateParams));
            } else if (this.url === '/subscriptions/plustrial') {
              this.setTitle(this.translate.instant('TITLE_APP_PREMIUM', interpolateParams));
            } else if (this.url === '/subscriptions/select') {
              this.setTitle(this.translate.instant('TITLE_APP_SELECT_BUY', interpolateParams));
            } else {
              this.setTitle(this.translate.instant('TITLE_APP_D_SIGNIN', interpolateParams));
            }
          } else {
            this.setTitle(this.translate.instant('TITLE_APP_DEV', interpolateParams));
          }
          break;
        case 'staging':
          if (!isLoggedIn) {
            if (this.url === '/subscriptions/free') {
              this.setTitle(this.translate.instant('TITLE_APP_FREE', interpolateParams));
            } else if (this.url === '/subscriptions/plustrial') {
              this.setTitle(this.translate.instant('TITLE_APP_PREMIUM', interpolateParams));
            } else if (this.url === '/subscriptions/select') {
              this.setTitle(this.translate.instant('TITLE_APP_SELECT_BUY', interpolateParams));
            } else {
              this.setTitle(this.translate.instant('TITLE_APP_S_SIGNIN', interpolateParams));
            }
          } else {
            this.setTitle(this.translate.instant('TITLE_APP_STAGING', interpolateParams));
          }
          break;
        case 'production':
          if (!isLoggedIn) {
            if (this.url === '/subscriptions/free') {
              this.setTitle(this.translate.instant('TITLE_APP_FREE', interpolateParams));
            } else if (this.url === '/subscriptions/plustrial') {
              this.setTitle(this.translate.instant('TITLE_APP_PREMIUM', interpolateParams));
            } else if (this.url === '/subscriptions/select') {
              this.setTitle(this.translate.instant('TITLE_APP_SELECT_BUY', interpolateParams));
            } else {
              this.setTitle(this.translate.instant('TITLE_APP_P_SIGNIN', interpolateParams));
            }
          } else {
            this.setTitle(this.translate.instant('TITLE_APP_PROD', interpolateParams));
          }
          break;
        case 'preprod':
          if (!isLoggedIn) {
            if (this.url === '/subscriptions/free') {
              this.setTitle(this.translate.instant('TITLE_APP_FREE', interpolateParams));
            } else if (this.url === '/subscriptions/plustrial') {
              this.setTitle(this.translate.instant('TITLE_APP_PREMIUM', interpolateParams));
            } else if (this.url === '/subscriptions/select') {
              this.setTitle(this.translate.instant('TITLE_APP_SELECT_BUY', interpolateParams));
            } else {
              this.setTitle(this.translate.instant('TITLE_APP_PP_SIGNIN', interpolateParams));
            }
          } else {
            this.setTitle(this.translate.instant('TITLE_APP_PP', interpolateParams));
          }
          break;
        case 'dr':
          if (!isLoggedIn) {
            if (this.url === '/subscriptions/free') {
              this.setTitle(this.translate.instant('TITLE_APP_FREE', interpolateParams));
            } else if (this.url === '/subscriptions/plustrial') {
              this.setTitle(this.translate.instant('TITLE_APP_PREMIUM', interpolateParams));
            } else if (this.url === '/subscriptions/select') {
              this.setTitle(this.translate.instant('TITLE_APP_SELECT_BUY', interpolateParams));
            } else {
              this.setTitle(this.translate.instant('TITLE_APP_DR_SIGNIN', interpolateParams));
            }
          } else {
            this.setTitle(this.translate.instant('TITLE_APP_DR', interpolateParams));
          }
          break;
        default:
          if (!isLoggedIn) {
            if (this.url === '/subscriptions/free') {
              this.setTitle(this.translate.instant('TITLE_APP_FREE', interpolateParams));
            } else if (this.url === '/subscriptions/plustrial') {
              this.setTitle(this.translate.instant('TITLE_APP_PREMIUM', interpolateParams));
            } else if (this.url === '/subscriptions/select') {
              this.setTitle(this.translate.instant('TITLE_APP_SELECT_BUY', interpolateParams));
            } else {
              this.setTitle(this.translate.instant('TITLE_APP_L_SIGNIN', interpolateParams));
            }
          } else {
            this.setTitle(this.translate.instant('TITLE_APP_LOCAL', interpolateParams));
          }
          break;
      }

      // To close the Alerts window if open while the user is logged out due to a session timeout.
      if (!isLoggedIn) {
        this.alertPanelShown = true;
        this.toggleNotifications();
      }
    });
  }

  ngAfterViewChecked() {
    // TODO: Check this hack out and see if we can do this the proper way.
    // This is for accessibility, the nav menu mode is "over" (default) so angular creates a focus trap
    // but we are handling the opening and closing of the menu and don't want a focus trap. If we change the mode
    // the opening/closing code/css must be redone to get the same effect.
    const elements = document.getElementsByClassName('cdk-visually-hidden cdk-focus-trap-anchor');
    while (elements.length > 0) {
      elements[0].parentNode.removeChild(elements[0]);
    }
  }

  ngAfterViewInit() {
    this.headerService.title
      .pipe(untilDestroyed(this))
      .subscribe(title => {
        this.title = title;
        this.cdr.detectChanges();
    });

    this.headerService.subtitle
      .pipe(untilDestroyed(this))
      .subscribe(subtitle => {
        this.subtitle = subtitle;
        this.cdr.detectChanges();
    });

    this.headerService.icon
      .pipe(untilDestroyed(this))
      .subscribe(icon => {
        this.icon = icon;
        this.cdr.detectChanges();
    });

    this.headerService.navMenuText
      .pipe(untilDestroyed(this))
      .subscribe(text => {
        this.navMenuText = text;
        this.cdr.detectChanges();
    });

    this.headerService.userFilter
      .pipe(untilDestroyed(this))
      .subscribe(enable => {
        this.showUserFilter = enable;
        this.cdr.detectChanges();
    });

    this.headerService.orgSelectorDefaultOption
      .pipe(untilDestroyed(this))
      .subscribe(defaultOption => {
        this.userSelectorDefaultOption = defaultOption;
        this.cdr.detectChanges();
    });
  }

  // This gets called from the login component with the logged-in user.
  setUserAttributes(user) {
    this.startAlerts(user);
    this.getExpiredSubscriptions(user);
    this.isAuthenticated = this.authService.isAuthenticated();
    this.showThemeSelector = this.isThemeSelectorVisible();
    this.userService.subscribeToOrgsChanges().subscribe(() => {
      // If the user is logged in and goes to the invitation/<uuid>, they get logged out
      // and this call get gets made, but gets a 401 and causes the invitation flow to fail
      // and the user goes to the login page instead of the invitation page.
      if (this.isAuthenticated) {
        this.store.dispatch(loadOrganizations());
      }
    });
  }

  public initializeTour(user: IManageUser, useAllSteps: boolean) {
      this.userTour.initializeTour(user, useAllSteps);
  }

  public setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  isThemeSelectorVisible(): boolean {
    const showThemeFeatureFlag = authHelper.getFeatureFlagFromToken('sts') ?? false;
    return this.configuration.showThemeSelector && showThemeFeatureFlag;
  }

  notificationsEnabled(user: IManageUser): boolean {
    return userExists(user) && authHelper.getFeatureFlagFromToken('not') && !isD3EFreeUser(user)
  }

  downloadEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'dwn');
  }

  onOrgSelectChanged(event) {
    // event doesn't have last value so we are managing last value here
    const newValue = event.value.toString();
    this.orgSelectChanged(newValue);
  }

  orgSelectChanged(newValue) {
    this.selectedOrgId = newValue;
    this.lastAllUsersId = this.currentAllUsersId;
    this.currentAllUsersId = newValue;
    if (this.currentAllUsersId === 'All') {
      localStorage.removeItem('fb_uid');
    } else {
      localStorage.setItem('fb_uid', newValue);
    }
    localStorage.removeItem('deviceSetSelection');
    this.headerService.setOrgSelectedChanged(newValue);
  }

  showNavMenuItem(user: IManageUser, featureFlag: FeatureFlags): boolean {
    return userExists(user) && authHelper.getFeatureFlagFromToken(featureFlag);
  }

  endpointsPageEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'epp');
  }

  secureSsdPageEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'ssp');
  }

  adminPageEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'adp');
  }

  threatHistoryEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'thp');
  }

  eventViewerEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'evp');
  }

  encryptionEventViewer(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'eev');
  }

  policyEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'pop');
  }

  subscriptionsEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'sup');
  }

  integrationsEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'inp');
  }

  groupsEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'grp');
  }

  organizationsEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'orp');
  }

  usersPageEnabled(user: IManageUser): boolean {
    let enabled = false;
    const uspPageFlag = authHelper.getFeatureFlagFromToken('usp') || false;
    const authed = this.authService.isAuthenticated();
    if (userExists(user) && authed && uspPageFlag &&
        (user.role == UserRole.ADMIN ||
         user.role == UserRole.HELP_DESK ||
         1 === user?.org_type.manage_child ||
         1 === user?.org_type.create_child) ) {
      enabled = true;
    }
    return enabled;
  }

  adminSettingsPageEnabled(user: IManageUser): boolean {
    let enabled = false;
    const adminSettingsPageFlag = authHelper.getFeatureFlagFromToken('asp') || false;
    if (userExists(user)
        && user?.role == UserRole.ADMIN
        && adminSettingsPageFlag) {
      enabled = true;
    }
    return enabled;
  }

  dynamicDashboardPageEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'ddp');
  }

  encryptionPageEnabled(user: IManageUser): boolean {
    return this.showNavMenuItem(user, 'enp');
  }

  memoizedIsD3EFreeUser = memo(user => isD3EFreeUser(user));
  memoizedIsReseller = memo(user => isReseller(user));
  memoizedIsMSP = memo(user => isMSP(user));
  memoizedIsCustomer = memo(user => isCustomer(user));
  memoizedIsResellerOrMSP = memo(user => isResellerOrMSP(user));

  showTourMenu(user: IManageUser): boolean {
    return authHelper.isUserTourEnabled(user);
  }

  getHelp(newWindow: boolean): void {
    if (newWindow) {
      window.open(this.supportInfo.url, '_blank', 'noopener noreferrer');
    } else {
      window.location.href = this.supportInfo.url;
    }
  }

  checkLoadedAndIsExpanded() {
    if (this.isExpanded) {
      return 'open-nav';
    } else {
      return 'close-nav';
    }
  }

  toggleSidebarExpansion() {
    if (this.isExpanded) {
      this.isExpanded = false;
      localStorage.setItem('nav-open', 'false');
    } else {
      this.isExpanded = true;
      localStorage.setItem('nav-open', 'true');
    }
  }

  getSideNavContentClass() {
    if (this.isNewsPage() && this.isAuthenticated) {
      if (this.isExpanded) {
        return 'widen-content';
      } else {
        return 'narrow-content';
      }
    }
    if (!this.isFullWidthPage()) {
      if (this.isExpanded) {
        return 'widen-content';
      } else {
        return 'narrow-content';
      }
    } else {
      return '';
    }
  }

  isNewsPage(): boolean {
    return this.url === '/cigent-news' || this.url === '/d3e-news';
  }

  isFullWidthPage() {
    return this.url.startsWith('/login') ||
      this.url === '/register' ||
      this.url.startsWith('/d3e-download') ||
      this.url.startsWith('/agent-download') ||
      this.url.startsWith('/download/') ||
      this.url.startsWith('/maintenance') ||
      this.url.startsWith('/invitation') ||
      this.url.startsWith('/cigent-invitation') ||
      this.url.startsWith('/d3e-invitation') ||
      this.url.startsWith('/verify-free') ||
      this.url.startsWith('/verify-email') ||
      this.url.startsWith('/downloaded-free/') ||
      this.url.startsWith('/subscriptions/plustrial') ||
      this.url.startsWith('/reset') ||
      this.url.startsWith('/okta/callback') ||
      this.url.startsWith('/azureservice/activate') ||
      this.isNewsPage();
  }

  onLogout() {
    this.stopAlerts();
    this.selectedOrgId = ALL_ORGS_ID;
    this.authService.logout();
  }

  initTranslate() {
    // Language Debug
    // console.log('Detected Language: ' + window.navigator.language);
    this.language = localStorage.getItem('language');

  }

  getExpiredSubscriptions(user: IManageUser) {
    if (this.authService.isAuthenticated() && !authHelper.isPasswordExpired() && this.subscriptionsEnabled(user)) {
      this.d3eService.get_subscriptions()
          .pipe(untilDestroyed(this))
          .subscribe(subscriptions => {
            this.showExpirationBanner = subscriptions
                .some(s => {
                  return s.type !== LicenseType.LICENSE_TYPE_FREE && isSubscriptionExpired(s) && s.used > 0;
                });
          });
        }
  }

  getSensorTypes() {
    if (this.authService.isAuthenticated() && !authHelper.isPasswordExpired()) {
      this.d3eService.getSensorTypes()
        .pipe(untilDestroyed(this))
        .subscribe(res => {
          const sensorTypes = {};
          res.sensorTypes.forEach(sensorType => {
            sensorTypes[sensorType.value] = sensorType.description || sensorType.displayValue;
          });
          this.sensorTypes = sensorTypes;
        });
    }
  }

  getAlerts() {
    if (this.authService.isAuthenticated() && !authHelper.isPasswordExpired()) {
      this.d3eService.get_home_alerts(this.alertsMaxDisplay)
        .pipe(untilDestroyed(this))
        .subscribe(res => {
            if (!res) {
              return;
            }
            this.alertsCount = res.count;
            this.alerts = res.alerts.map(alert => {
              alert.event_dttm = new Date(alert.event_dttm);
              return alert;
            });
          }
        );
    } else {
      this.stopAlerts();
    }
  }

  clearAllAlerts() {
    this.d3eService.clear_all_alerts()
      .subscribe(() => {
          this.getAlerts();
          this.dismissAllPanel = false;
          this.alertsMaxDisplay = 5;
          this.alertPanelShown = false;
        }
    );
  }

  dismissAlert(alert: IActivelockAlert): void {
    alert.dismissed = 1;
    if (this.alerts.length === 1) {
      this.backdropClicked();
    }
    this.d3eService.clear_alert(alert.id)
      .subscribe(() => {
          this.getAlerts();
          this.hideDetails();
        }
    );
  }

  showMoreAlerts() {
    this.alertsMaxDisplay = this.alertsMaxDisplay + 8;
    this.getAlerts();
  }

  backdropClicked() {
    this.alertPanelShown = false;
    this.dismissAllPanel = false;
    this.hideDetails();
  }

  showDetails(i: number) {
    this.details = this.alerts[i];
    this.detailsShown = true;
    this.notificationDetailsFocusTrap = this.focusTrapFactory.create(this.detailsDiv.nativeElement);
    setTimeout(() => {
      this.notificationDetailsFocusTrap.focusInitialElement();
    }, 100);
  }

  hideDetails() {
    this.detailsShown = false;
    if ( this.notificationDetailsFocusTrap != null ) {
      this.notificationDetailsFocusTrap.destroy();
    }
    this.notificationDetailsFocusTrap = null;
    if (this.alertPanelShown) {
      this.notificationsFocusTrap.focusInitialElement();
    }
  }

  getActiveLock(activeLock): number[] {
    if (typeof activeLock === 'object') {
      return activeLock;
    }
    return [1];
  }

  getDescriptions(activeLock) {
    return this.getActiveLock(activeLock).map(id => {
      const desc = this.sensorTypes[id];
      if (desc) {
        return desc;
      }
      return `Threat Detected (sensor=${id})`;
    });
  }

  handleVisibilityChange(user: IManageUser) {
    if (document.hidden) {
      this.stopAlerts();
    } else {
      this.startAlerts(user);
    }
  }

  stopAlerts() {
    this._stop.next();
    this.running = false;
  }

  startAlerts(user: IManageUser) {
    // The check for this.running is an extra precaution because repeatWhen
    // starts a new observable and we can get multiple calls to getAlerts.
    if (!this.running && this.notificationsEnabled(user)) {
      this.running = true;
      this._start.next();
    }
  }

  getCompanyDisplayName(group) {
    if (group.name && group.contact_email) {
      if (group.name === group.contact_email) {
        return group.name;
      }
      return `${group.name} (${group.contact_email})`;
    }
    if (group.name) {
      return group.name;
    }
    if (group.contact_email) {
      return group.contact_email;
    }
    return '_' + group.id + '_';
  }

  toggleNotifications() {
    this.alertPanelShown = !this.alertPanelShown;
    if (this.alertPanelShown) {
      this.getSensorTypes();
      this.notificationsFocusTrap = this.focusTrapFactory.create(this.alertPanel.nativeElement);
      setTimeout(() => {
        this.notificationsFocusTrap?.focusInitialElement();
      }, 100);
    } else {
      this.notificationsFocusTrap?.destroy();
      this.notificationsFocusTrap = null;
    }
    this.hideDetails();
    this.dismissAllPanel = false;
  }

  onNewsClick() {
    this.dialog.open(NewsComponent, {
      width: 'auto',
      height: 'auto',
      minWidth: '375px',
      data: {
        consoleVersion: this.consoleVersion,
        latestCigentVersion: this.latestCigentVersion,
        restApiVersion: this.restApiVersion,
        pbaVersions: this.pbaVersions,
        msiVersion: this.msiVersion,
        winCliVersions: this.winCliVersions,
        ubuntuCliVersions: this.ubuntuCliVersions,
        rhelCliVersions: this.rhelCliVersions,
        winFdeVersions: this.winFdeVersions,
        ubuntuFdeVersions: this.ubuntuFdeVersions,
        rhelFdeVersions: this.rhelFdeVersions,
        winAgentVersions: this.winAgentVersions,
        ubuntuAgentVersions: this.ubuntuAgentVersions,
        rhelAgentVersions: this.rhelAgentVersions,
      }
    });
  }

  downloadCigent() {
    this.d3eService.get_subscriptions()
        .pipe(untilDestroyed(this))
        .subscribe((subscriptionsResponse: ISubscription[]) => {
              let uuid = null;
              const subscription: ISubscription = getFirstValidSubscription(subscriptionsResponse);
              if (subscription != null) {
                uuid = subscription.uuid;
              }
              this.d3eService.get_d3e_software_version(uuid)
                  .pipe(untilDestroyed(this))
                  .subscribe((version: ISoftwareVersion) => {
                    if (!uuid) {
                      this.downloadGeneric(version);
                    } else {
                      let system = '';
                      if (version.system) {
                        system = version.system;
                      }
                      const subscription_filename = get_uuid_filename(version, system, uuid);
                      const installerName = subscription_filename.trim().split('/').pop();
                      multiDownload([version.path], {
                        rename: () => installerName
                      });
                    }
                  });
            },
            () => {
              // allow download of generic installer even in case of error getting sub.
              // must still get the path
              // this.downloadGeneric(version);
              this.d3eService.get_d3e_software_version()
                  .pipe(untilDestroyed(this))
                  .subscribe((version: ISoftwareVersion) => {
                        this.downloadGeneric(version);
                      }
                  );
            });
  }

  private downloadGeneric(version: ISoftwareVersion) {
    multiDownload([version.path]);
    this.actionService.postDownload(version.path, 'download', null, null);
  }

  onThemeClicked($event) {
    $event.stopPropagation();
  }

  onThemeChanged(checked: boolean) {
    const theme = checked ? 'dark' : 'light';
    this.themeService.setTheme(theme);
  }

  refreshClicked() {
    this.headerService.refresh$.next(true);
  }

  showRequestsDialog() {
    this.dialog.open(SecurityRequestsStatusListDialogComponent, {}).afterClosed().subscribe();
  }

  protected readonly OrgType = OrgType;
  protected readonly UserRole = UserRole;
  protected readonly SecurityApproverState = SecurityApproverState;
}
